import * as React from 'react';
import './_CustomPropertiesElements.scss';
import { ListItem } from '../../../../../base/components/ListItem/ListItem';
import { Button, Icon } from 'kui';
import { ESettingsSectionType } from '../../../../../base/components/SettingsSection/types';
import { ICustomPropertiesElementsProps } from './types';
import { getAsidePanelTooltip } from '../../../../asidePanel/helpers/getAsidePanelTooltip';
import { CUSTOM_BUTTON_REMOVE_TOOLTIP } from './constants';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { AsidePanelContext } from '../../../../asidePanel/components/AsidePanel/constants';
import { CLASS_LIST_ITEM_CONTAINER } from '../../../../../base/components/ListItem/constants';
import { CLASS_CARD_CUSTOM } from '../constants';
import { TCustomPropertyId } from '../../../../../../../types/types';
import { ORDER_STEP } from '../../../../../../../const';
import { CustomPropertiesHideButton } from '../CustomPropertiesHideButton/CustomPropertiesHideButton';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { isEqual } from 'underscore';
import {
    CustomPropertiesFormHOC
} from '../../../../../base/components/CustomProperties/hocs/CustomPropertiesFormHOC/CustomPropertiesFormHOC';
import {
    CustomPropertyHOC
} from '../../../../../base/components/CustomProperties/hocs/CustomPropertyHOC/CustomPropertyHOC';
import {
    ICustomPropertiesElement
} from '../../../../../base/components/CustomProperties/components/CustomProperty/types';
import { getCustomPropertyValid } from '../../../../../base/components/CustomProperties/helpers/getCustomPropertyValid';
import { ECustomPropertyType } from '../../../../../../../store/customProperties/types';

//@ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CardCustomPropertiesElements`) : null;

export function CustomPropertiesElements ({
    elements,
    isAddOpened,
    isEdit,
    isHideEmpty,
    sectionType,
    onConfirmDelete,
    onEdit,
    onSave,
    setChangedElements,
}: ICustomPropertiesElementsProps) {
    const { cardId, boardId, } = React.useContext(AsidePanelContext);

    if (analyzeRender) analyzeRender.call(`CardCustomPropertiesElements ${cardId}`);

    const isReadonly = sectionType === ESettingsSectionType.READONLY || sectionType === ESettingsSectionType.DISALLOW;
    const isPopup = sectionType === ESettingsSectionType.POPUP;

    const className = CLASS_CARD_CUSTOM + '__items';
    const classItem = className + '-item';

    function getEmptyCount () {
        if (!elements.length) return 0;

        return elements.reduce((empty, element) => {
            if (!element.pinned && (element.value === null || element.value === undefined)) empty++;
            return empty;
        }, 0);
    }

    const [elementsState, setElements] = React.useState(getElementsClone(elements));
    const [isHide, setHide] = React.useState(isHideEmpty);

    function getElementsClone (
        elements: ICustomPropertiesElement[],
        cleanChanges?: boolean,
    ): ICustomPropertiesElement[] {
        return elements.map(element => {
            let changed: Partial<ICustomPropertiesElement> = {};
            const elementState = !!elementsState && elementsState.find(item => item.id === element.id);
            if (!cleanChanges && elementState) {
                if (elementState.isChanged) {
                    changed = {
                        ...element,
                        isChanged: true,
                        value: elementState.value,
                    };
                }
                changed.removed = elementState.removed;
            }

            return {
                ...element,
                options: element.options ? element.options.map(option => ({...option})) : [],
                ...changed
            };
        });
    };

    const onSortOptionsHandler = (
        propertyId: TCustomPropertyId,
        sourceIndex: number,
        index: number,
    ) => {
        const element = elementsState.find(item => item.id === propertyId);
        const options = [...element.options];
        const option = options.splice(sourceIndex, 1)[0];
        options.splice(index, 0, option);

        setElements(elementsState.map(item => {
            return item.id === propertyId
                ? {
                    ...item,
                    options
                }
                : item;
        }));
    };

    const onEnter = () => {
        if (!getCustomPropertyValid(elementsState, setElements)) return;
        onSave(elementsState);
    };

    const onSortHandler = (
        id: TCustomPropertyId,
        index: number,
    ) => {
        let orderNumber: number;
        const elementsNew = elementsState.filter(item => item.id !== id);
        if (index === 0) {
            orderNumber = elementsNew[0].orderNumber + ORDER_STEP;
        } else if (index === elementsState.length - 1) {
            orderNumber = elementsNew[elementsNew.length - 1].orderNumber - ORDER_STEP;
        } else {
            orderNumber = (elementsNew[index - 1].orderNumber + elementsNew[index].orderNumber) / 2;
        }
        setElements(elementsState.map(item => {
            return item.id === id
                ? {
                    ...item,
                    orderNumber,
                }
                : item;
        }).sort((prop1, prop2) => {
            return prop2.orderNumber - prop1.orderNumber; // больший order сверху
        }));
    };

    function onDragEnd(result: DropResult) {
        if (
            !result.destination ||
            result.destination.index === result.source.index
        ) {
            // do nothing
        } else {
            onSortHandler(result.draggableId, result.destination.index);
        }
    }

    const onDeleteHandler = (id: TCustomPropertyId) => {
        onConfirmDelete(id)
            .then((result) => {
                if (result) {
                    setElements(elementsState.map(item => {
                        return item.id === id
                            ? {
                                ...item,
                                removed: true
                            }
                            : item;
                    }));
                }
            })
    };

    const onChangeProperty = (
        property: ICustomPropertiesElement,
        validate?: boolean,
    ) => {
        const newElements = [...elementsState];
        const index = newElements.findIndex(item => item.id === property.id);
        if (index === -1) return;
        newElements.splice(index, 1, property);
        setElements(newElements);
        if (validate && newElements.find(el => el.error)) getCustomPropertyValid(elementsState, setElements);
    };

    function castValue (value: string) {
        return value === undefined || value === '' ? null : value;
    }

    React.useEffect(() => {
        if (!isAddOpened) return;
        setHide(false);
    }, [isAddOpened]);

    React.useEffect(() => {
        if (isEdit) {
            let isError = false;
            for (let i = 0; i < elementsState.length; i++) {
                if (
                    elementsState[i].error ||
                    elementsState[i].type === ECustomPropertyType.SELECT &&
                    (!elementsState[i].options || !elementsState[i].options.find(option => option.value))
                ) {
                    isError = true;
                    break
                }
            }
            const currentElements = getElementsClone(elements, true).map(({ name, orderNumber, options, removed, type }) => {
                return {
                    name,
                    orderNumber,
                    options,
                    removed,
                    type,
                };
            });
            const newElements = elementsState.map(({ name, orderNumber, options, removed, type }) => {
                return {
                    name,
                    orderNumber,
                    options,
                    removed,
                    type,
                };
            });
            setChangedElements(isEqual(newElements, currentElements) || isError ? null : elementsState);
        }
    }, [isEdit, elementsState]);

    React.useEffect(() => {
        if (
            !isEdit &&
            elements.length === elementsState.length &&
            !elementsState.find(item => item.removed)
        ) {
            return;
        }
        setElements(getElementsClone(elements, isEdit || !!elementsState.find(item => item.removed)));
    }, [elements, isEdit]);

    const isShowHideButton = isPopup || isReadonly || !elements || !elements.length ? null : getEmptyCount() ? isHideEmpty : null;

    return (<div className={className} tabIndex={-1}>
        {isEdit ?
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={String(cardId)}>
                    {(provided, snapshot) => (
                        <div
                            className={CLASS_CARD_CUSTOM}
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            {elementsState.map((element, index) => {
                                if (element.removed) return null;

                                return (
                                    <Draggable
                                        draggableId={element.id}
                                        index={index}
                                        isDragDisabled={isReadonly || isPopup || elements.length === 1}
                                        key={element.id}
                                    >
                                        {(provided, snapshot) => {
                                            const actions = isReadonly ? null
                                                : <Button
                                                    variant={'icon'}
                                                    tooltip={ getAsidePanelTooltip({
                                                        direction: 'up-left',
                                                        value: CUSTOM_BUTTON_REMOVE_TOOLTIP,
                                                    })
                                                    }
                                                    onClick={() => onDeleteHandler(element.id)}
                                                >
                                                    <Icon size={24} xlink={'delete'} />
                                                </Button>;

                                            return (
                                                <div
                                                    className={CLASS_LIST_ITEM_CONTAINER}
                                                    key={element.id}
                                                    {...provided.draggableProps}
                                                    ref={provided.innerRef}
                                                >
                                                    <ListItem
                                                        actions={actions}
                                                        className={`
                                                            ${classItem}
                                                            ${classItem + '--' + element.type.toLocaleLowerCase()}
                                                        `}
                                                        isDraggable={!isReadonly && !isPopup && elements.length > 1}
                                                        text={<CustomPropertiesFormHOC
                                                            cardId={cardId}
                                                            boardId={boardId}
                                                            element={element}
                                                            isPopup={isPopup}
                                                            isCloseOnAdd={!isPopup}
                                                            onChange={(name, type, options) => onChangeProperty({
                                                                ...element,
                                                                name,
                                                                type,
                                                                options,
                                                            }, true)}
                                                            onSortOptions={onSortOptionsHandler}
                                                            onEnter={onEnter}
                                                        />}
                                                        {...provided.dragHandleProps}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        : <div className={CLASS_CARD_CUSTOM}>
            {elementsState.map(element => {
                const elementOriginal = elements.find(item => item.id === element.id);
                const isEmptyValue = elementOriginal && castValue(elementOriginal.value) === null && !element.isRequired;
                if (isReadonly || isHideEmpty && isHide) {
                    const hideElement = (isReadonly && isEmptyValue) || (isHideEmpty && isHide && isEmptyValue && !elementOriginal.pinned);
                    if (hideElement) {
                        return null;
                    }
                }
                return <CustomPropertyHOC
                    key={element.id}
                    cardId={cardId}
                    customProperty={element}
                    isReadonly={isReadonly}
                    isPopup={isPopup}
                    onChange={onChangeProperty}
                    onCancel={() => setElements(getElementsClone(elements, true))}
                    onEdit={onEdit}
                />;
            })}
            {!isReadonly && isShowHideButton &&
                <CustomPropertiesHideButton
                    isHide={isHide}
                    onClick={() => setHide(!isHide)}
                />
            }
        </div>
    }</div>);
};
